import React, { useEffect, useState } from 'react';
import { getGpoId } from '@v2/utils/GPOUtils';
import { useLocation } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useGetCustomerPaymentMethods } from '@/ar/hooks/queries/CustomerQueries.hook';
import { useSupplierStore } from '@/ar/stores/SupplierStore';
import { CreatePaymentMethod } from '@/ar/components/shared/modals/CreatePaymentMethod/CreatePaymentMethod';
import { useCreatePaymentMethodStore } from '@/ar/stores/CreatePaymentMethodStore';
import { CreatePaymentMethodSteps } from '@/ar/components/shared/modals/CreateAdyenPaymentMethod/CreateAdyenPaymentMethodConstants';
import { MandateStatus, GetPaymentMethodResponse } from '@/ar/network/AccountsReceivable.network';

const sortPendingMandates = (a: GetPaymentMethodResponse, b: GetPaymentMethodResponse): number => {
    // Handle cases where mandate_created_at might be undefined
    if (!a.mandate_created_at && !b.mandate_created_at) return 0;
    if (!a.mandate_created_at) return 1;
    if (!b.mandate_created_at) return -1;

    // Compare dates when both are defined
    return new Date(b.mandate_created_at).getTime() - new Date(a.mandate_created_at).getTime();
};

export const MandateAcceptReminder: React.FC = () => {
    const flags = useFlags();
    const showAdyenBankCustomerPortal = flags?.showAdyenBankCustomerPortal;
    const { supplierLoginData } = useSupplierStore();
    const { supplier_id: supplierID, customer_id: customerID } = supplierLoginData;
    const gpoId = getGpoId(supplierLoginData);
    const [isCreatePaymentMethodModalOpen, setIsCreatePaymentMethodModalOpen] = useState(false);
    const { setCurrentStep, setMandateId, setCreatePaymentMethodModalOpen, openAcceptMandateModal } = useCreatePaymentMethodStore();
    const { pathname } = useLocation();
    const { data: paymentMethodsResponse } = useGetCustomerPaymentMethods({
        customerID,
        supplierID,
        gpoId,
        alwaysRefetch: true
    });
    const pendingMandates = paymentMethodsResponse?.payment_methods
        .filter((paymentMethod) => paymentMethod?.mandate_status === MandateStatus.PENDING)
        .sort(sortPendingMandates);
    const hasPendingMandate = pendingMandates?.length > 0;
    const isSettingsPage = pathname.includes('customer_settings');
    // Check for pending mandates on route change
    useEffect(() => {
        if (hasPendingMandate && !isSettingsPage) {
            setCreatePaymentMethodModalOpen(false);
            setIsCreatePaymentMethodModalOpen(true);
            setCurrentStep(CreatePaymentMethodSteps.BANK_ACCOUNT_ACCEPT_MANDATE);
            setMandateId(pendingMandates[0].mandate_id);
        }
        //
        if (hasPendingMandate && isSettingsPage) {
            openAcceptMandateModal(pendingMandates[0].mandate_id);
        }
    }, [pathname, hasPendingMandate, isSettingsPage]);

    // checking the settings page since there's an instance there of the CreatePaymentMethod we're handling that in useEffect above
    if (!hasPendingMandate || isSettingsPage || !showAdyenBankCustomerPortal) {
        return null;
    }

    return (
        <CreatePaymentMethod isOpen={isCreatePaymentMethodModalOpen} handleClose={(): void => setIsCreatePaymentMethodModalOpen(false)} customerID={customerID} />
    );
};
